import {useEffect, useMemo, useState} from "react";
import "@/meetingCards.css";

export function meetingDate(value) {
  // Legacy timestamps without an offset were stored in UTC, never browser time.
  return new Date(/^\d{4}-\d\d-\d\dT/.test(value) && !/(Z|[+-]\d\d:\d\d)$/i.test(value) ? value + "Z" : value);
}

export function formatMeetingTime(value) {
  const date = value && meetingDate(value);
  return !date || Number.isNaN(date.getTime()) ? "Time unavailable" : date.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
  });
}

export function meetingPresentation(meeting, now) {
  const start = meetingDate(meeting.confirmed_time).getTime();
  const opens = meeting.join_opens_at && meetingDate(meeting.join_opens_at).getTime();
  const closes = meeting.join_closes_at && meetingDate(meeting.join_closes_at).getTime();
  const pendingRole = meeting.proposal_role || meeting.initiated_by_role || "buyer";
  const states = {
    requested: ["Requested", `Awaiting ${pendingRole === "exhibitor" ? "buyer" : "exhibitor"} confirmation`, "not_confirmed"],
    rescheduled: ["Rescheduled", `Awaiting ${pendingRole === "exhibitor" ? "buyer" : "exhibitor"} confirmation`, "rescheduled"],
    declined: ["Declined", "Meeting declined", "declined"],
    cancelled: ["Cancelled", "Meeting cancelled", "cancelled"],
    completed: ["Ended", "Meeting ended", "completed"],
  };
  let [label, description, kind] = states[meeting.status] || ["Unavailable", "Meeting time unavailable", "not_confirmed"];
  if (meeting.status === "confirmed" && Number.isFinite(start)) {
    [label, description, kind] = ["Confirmed", "Meeting confirmed", "not_confirmed"];
    if (opens && closes && Number.isFinite(now)) {
      if (now >= closes) [label, description, kind] = ["Ended", "Meeting ended", "ended"];
      else if (now >= start) [label, description, kind] = ["In progress", "Meeting in progress", "open"];
      else if (now >= opens) [label, description, kind] = ["Starting soon", "Meeting starting soon", "open"];
      else kind = "not_yet";
    }
  }
  return {label, description, kind, minsUntil: Math.max(1, Math.ceil((opens - now) / 60000)),
    remaining: meeting.status === "confirmed" && start > now ? start - now : null};
}

function countdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const pad = value => String(value).padStart(2, "0");
  return `${pad(Math.floor(total / 86400))}d : ${pad(Math.floor(total / 3600) % 24)}h : ${pad(Math.floor(total / 60) % 60)}m : ${pad(total % 60)}s`;
}

export default function MeetingCard({meeting, counterpart, badge, children, onView}) {
  // Advance the server's sample with a monotonic clock. This is presentation
  // only: the existing token endpoint authorizes every actual room join.
  const clock = useMemo(() => ({server: meeting.server_time ? meetingDate(meeting.server_time).getTime() : Date.now(), received: performance.now()}), [meeting.server_time]);
  const [, tick] = useState(0);
  useEffect(() => {
    if (meeting.status !== "confirmed") return;
    const close = meeting.join_closes_at ? meetingDate(meeting.join_closes_at).getTime() : Infinity;
    const now = () => clock.server + performance.now() - clock.received;
    if (now() >= close) return;
    const timer = setInterval(() => {tick(n => n + 1);if (now() >= close) clearInterval(timer);}, 1000);
    return () => clearInterval(timer);
  }, [meeting.status, meeting.join_closes_at, clock]);
  const state = meetingPresentation(meeting, clock.server + performance.now() - clock.received);
  const when = meeting.status === "cancelled" ? meeting.previous_time || meeting.requested_time : meeting.confirmed_time || meeting.requested_time;
  const selected = new URLSearchParams(window.location.search).get("meeting") === meeting.id;
  useEffect(() => {
    if (selected && !onView) document.getElementById(`meeting-card-${meeting.id}`)?.scrollIntoView({block: "center"});
  }, [selected, meeting.id, onView]);
  return <section className={`meeting-card${selected ? " meeting-selected" : ""}`} id={onView ? undefined : `meeting-card-${meeting.id}`} data-testid={`meeting-card-${meeting.id}`} data-meeting-id={meeting.id} data-status={meeting.status}>
    <div className="meeting-card-top"><span className="meeting-eyebrow">Private meeting</span><span className={`meeting-state meeting-state-${state.kind}`}>{state.label}</span></div>
    <h3>{counterpart || "Meeting participant"} {badge}</h3>
    {meeting.status === "rescheduled" && <div className="meeting-previous">Previous: {meeting.previous_time ? formatMeetingTime(meeting.previous_time) : "Not recorded for this earlier request"}</div>}
    <time dateTime={when || undefined}>{meeting.status === "rescheduled" && "New: "}{when ? formatMeetingTime(when) : "Time to be agreed"}</time>
    <p className="meeting-description">{state.description}</p>
    {state.remaining !== null && <div className="meeting-countdown" data-testid="meeting-countdown"><small>Starts in</small><span>{countdown(state.remaining)}</span></div>}
    {typeof children === "function" ? children(state) : children}
    {onView && <button className="meeting-view" onClick={onView}>{meeting.status === "requested" ? "View Request" : "View Meeting"} <span aria-hidden="true">↗</span></button>}
  </section>;
}
